import React from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import { Close, Download as DownloadIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import authService, { StoreLogFile, StoreLogsResponse } from '../../services/authService';
import api from '../../interceptors/axiosConfig';
import API_URLS from '../../config/apiConfig';
import { formatUtcAssumedDateTimeMMDDYYYY } from '../../utils/formatUtils';
import { adminBorders, adminColors, adminTypography } from '../../styles/adminDesignTokens';
import {
  adminCombineSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminEmptyStateSx,
  adminIconActionButtonSx,
  adminSurfaceSx,
} from '../../styles/adminComponentStyles';

type StoreLogsDialogProps = {
  open: boolean;
  storeId: string | null | undefined;
  onClose: () => void;
};

const StoreLogsDialog: React.FC<StoreLogsDialogProps> = ({ open, storeId, onClose }) => {
  const [logsLoading, setLogsLoading] = React.useState<boolean>(false);
  const [logsError, setLogsError] = React.useState<string | null>(null);
  const [logsData, setLogsData] = React.useState<StoreLogsResponse | null>(null);
  const [logsPage, setLogsPage] = React.useState<number>(0);
  const logsRowsPerPage = 100;

  const formatLogTimestamp = React.useCallback((value?: string | null) => {
    return formatUtcAssumedDateTimeMMDDYYYY(value, { fallback: '—' });
  }, []);

  const formatFileSize = React.useCallback((bytes?: number | null) => {
    if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '—';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let size = bytes / 1024;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
  }, []);

  const fetchStoreLogs = React.useCallback(async (page: number) => {
    if (!storeId) {
      setLogsError('Store ID is missing');
      return;
    }
    setLogsLoading(true);
    setLogsError(null);
    try {
      const offset = page * logsRowsPerPage;
      const data = await authService.getStoreLogs(storeId, logsRowsPerPage, offset);
      setLogsData(data);
    } catch (error) {
      console.error('Failed to fetch store logs', error);
      setLogsError('Failed to load store logs. Please try again.');
    } finally {
      setLogsLoading(false);
    }
  }, [logsRowsPerPage, storeId]);

  React.useEffect(() => {
    if (open) {
      setLogsPage(0);
      setLogsData(null);
      setLogsError(null);
    }
  }, [fetchStoreLogs, open]);

  React.useEffect(() => {
    if (open) {
      fetchStoreLogs(logsPage);
    }
  }, [fetchStoreLogs, logsPage, open]);

  const handleDownloadLogFile = async (file: StoreLogFile) => {
    try {
      const downloadUrl = API_URLS.COMPLETE_STORE_LOG_URL(file.download_url);
      const response = await api.get(downloadUrl, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = file.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download log file', error);
      toast.error('Failed to download log file. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: adminCombineSx(adminDialogSx, { height: '70vh' }) }}
    >
      <DialogTitle sx={adminDialogTitleSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ ...adminTypography.cardTitle, color: adminColors.text }}>
            Download Logs
          </Typography>
          <IconButton onClick={onClose} size="small" sx={adminIconActionButtonSx}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={adminCombineSx(adminDialogContentSx, { display: 'flex', flexDirection: 'column', height: '100%' })}>
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
          {logsLoading ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : logsError ? (
            <Typography sx={{ ...adminTypography.body, color: adminColors.danger, mt: 1 }}>
              {logsError}
            </Typography>
          ) : (logsData?.files?.length ?? 0) === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={adminCombineSx(adminEmptyStateSx, { minHeight: 96 })}>
                <Typography className="AdminEmptyState-title">
                  No log files found.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {(logsData?.files ?? []).map((file) => (
                <Box
                  key={file.filename}
                  sx={adminCombineSx(adminSurfaceSx, {
                    alignItems: 'center',
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.25,
                  })}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }} noWrap>
                      {file.filename}
                    </Typography>
                    <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, display: 'block' }}>
                      Uploaded at {formatLogTimestamp(file.uploaded_at)}
                    </Typography>
                    <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, display: 'block' }}>
                      Size: {formatFileSize(file.size_bytes)}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => handleDownloadLogFile(file)}
                    sx={adminIconActionButtonSx}
                    aria-label={`download ${file.filename}`}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
        <Box sx={{ backgroundColor: adminColors.surface, borderTop: adminBorders.divider, flexShrink: 0, mt: 1, pt: 1 }}>
          <TablePagination
            component="div"
            count={logsData?.total ?? 0}
            page={logsPage}
            onPageChange={(_event, nextPage) => setLogsPage(nextPage)}
            rowsPerPage={logsRowsPerPage}
            rowsPerPageOptions={[logsRowsPerPage]}
            onRowsPerPageChange={() => {}}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(StoreLogsDialog);
